/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Card, Carousel, Icon, Loader } from '@scuf/common';
import { CompanySelectionModal } from 'components';
import { SsoDTO } from 'models/user/sso';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { SSO, USER_MANAGER } from 'resources';
import { UserPortal, WelcomeMessageScreens } from 'models/user/userAccount';
import useFetch from 'helpers/hooks/useFetch';
import usePreventClickjacking from 'helpers/hooks/usePreventingClickjacking';
import i18n from 'i18n';
import useUser from 'helpers/hooks/useUser';
import 'react-toastify/dist/ReactToastify.min.css';

interface ManualFile {
    id: number;
    displayName: string;
    storedFileName: string;
    section: 'USER';
}

interface HomeSlide {
    id: string;
    image: string;
    title: string | null;
    subtitle: string | null;
    visible: boolean;
    button: string | null;
}

function HomePage() {
    const { t } = useTranslation(['home', 'common', 'certified']);
    const section = "msg-home";
    const [modalState, setModalState] = useState<'pending' | 'email' | 'company' | undefined>();
    const location = useLocation();
    const [userLanguage, setUserLanguage] = useState<string>();
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('code');
    const [user, setUser] = useState<UserPortal>({} as UserPortal);
    const { user: userDetails, getUser, loadingUser } = useUser();
    const urlSso = process.env.REACT_APP_LOCAL !== undefined ? SSO.generateQAToken : SSO.generateToken;
    const [, getToken, loading] = useFetch<SsoDTO, any>({
        url: urlSso,
        method: 'POST'
    });

    const defaultWelcomeMessages: WelcomeMessageScreens = {
        homepage: false,
        repair: false,
        spare: false,
        manufatura: false,
        hsl: false,
        bslbr: false
    };

    const [hasSeenMessage, setHasSeenMessage] = useState(() => {
        const storedValue = sessionStorage.getItem('welcomeMessageSeen');
        if (!storedValue) {
            sessionStorage.setItem(
                'welcomeMessageSeen',
                JSON.stringify(defaultWelcomeMessages)
            );

            return false;
        }

        try {
            const welcomeMessageSeen: WelcomeMessageScreens =
                JSON.parse(storedValue);

            return welcomeMessageSeen.homepage === true;
        } catch {
            return false;
        }
    });


    const [show, setShow] = useState(false);
    const [fileUrl, setFileUrl] = useState<string>();

    const [dataLanguage, fetchLanguage, loadingLanguage] = useFetch<{ ssoId: string }>({
        url: SSO.getCultureInfo,
        method: 'GET'
    });

    const [, fetchFiles] = useFetch<void, ManualFile>({
        url: `${USER_MANAGER.getIdPortalMensagem}?section=${section}&language=${userLanguage}`,
        method: 'GET'
    });


    const slides: HomeSlide[] = [
        {
            id: 'welcome-brady',
            image: require('../../assets/fundo-brady5.png'),
            title: t('carousel.welcome.title-brady'),
            subtitle: t('carousel.welcome.info-brady'),
            visible: true,
            button: t('carousel.welcome.button')
        },
        {
            id: 'pss-brady',
            image: require('../../assets/PSS.png'),
            title: null,
            subtitle: null,
            visible: true,
            button: null
        },
        {
            id: 'repair',
            image: require('../../assets/fundo-brady4.png'),
            title: t('carousel.repair.title'),
            subtitle: t('carousel.repair.info'),
            visible: true,
            button: null
        },
        {
            id: 'sales',
            image: require('../../assets/fundo-brady3.jpg'),
            title: t('carousel.sales.title'),
            subtitle: t('carousel.sales.info'),
            visible: i18n.language !== 'es',
            button: null
        }
    ];

    useEffect(() => {
        document.title = 'Home';
    }, []);

    useEffect(() => {
        handleToken();
    }, []);

    useEffect(() => {

        if (dataLanguage?.data) {
            i18n.changeLanguage(dataLanguage.data);
        }
    }, [dataLanguage]);

    useEffect(() => {
        if (userDetails && Object.keys(userDetails).length > 0) {
            handleLanguage(userDetails.ssoid);
        }
    }, [userDetails]);

    useEffect(() => {
        const language = dataLanguage?.data;

        if (!language) {
            return;
        }

        const applyLanguage = async () => {
            await i18n.changeLanguage(language);
            setUserLanguage(language);
        };

        applyLanguage();
    }, [dataLanguage]);

    useEffect(() => {
        if (
            !userLanguage ||
            !userDetails ||
            Object.keys(userDetails).length === 0
        ) {
            return;
        }

        loadFiles();
    }, [userLanguage, userDetails]);

    const loadFiles = async (): Promise<void> => {
        try {
            if (hasSeenMessage === true) {
                return;
            }
            await fetchFiles(undefined, (resp) => {
                const { id } = resp.data ?? {};
                if (!id) {
                    return;
                }
                setFileUrl(`${USER_MANAGER.downloadManualFileById}?id=${id}`);
                setShow(true);
            });
        } catch (error: any) {
            if (error.status === 401) {
                await getUser();
            } else {
                console.error(t('manual.fetchFilesError'));
            }
        }
    };



    async function handleToken() {
        const data: SsoDTO = {
            code: process.env.REACT_APP_LOCAL !== undefined ? (process.env.REACT_APP_EID ?? null) : code,
            redirect_uri: process.env.REACT_APP_SSO_REDIRECT_URI!,
            grant_type: 'authorization_code'
        };

        await getToken(data, async curr => {
            if (curr.message === '1') {
                setUser({
                    ...user,
                    ssoId: curr.data.sub,
                    name: curr.data.given_Name + ' ' + curr.data.family_Name,
                    email: curr.data.email
                });
                setModalState('company');
            } else if (curr.message === '2') {
                setModalState('pending');
            } else {
                await fetch(urlSso, {
                    method: 'GET',
                    credentials: 'include' // Necessary to ensure cookies are sent and received
                });
                await getUser();
            }
        });
    }

    async function handleLanguage(ssoId: string) {
        await fetchLanguage({ ssoId });
    }

    function handleMessage() {
        const storedValue = sessionStorage.getItem('welcomeMessageSeen');

        let welcomeMessageSeen: WelcomeMessageScreens = defaultWelcomeMessages;

        if (storedValue) {
            try {
                welcomeMessageSeen = JSON.parse(storedValue);
            } catch {
                welcomeMessageSeen = defaultWelcomeMessages;
            }
        }
        sessionStorage.setItem(
            'welcomeMessageSeen',
            JSON.stringify({
                ...welcomeMessageSeen,
                homepage: true
            })
        );
        setHasSeenMessage(true);
        setShow(false);
    }
    usePreventClickjacking();

    return (
        <>
            {show && userDetails && (hasSeenMessage === false) && Object.keys(userDetails).length > 0 && i18n.languages && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}
                    onClick={() => handleMessage()}
                >
                    <img
                        style={{ maxWidth: '800px', maxHeight: '800px' }}
                        src={fileUrl}
                        onClick={e => e.stopPropagation()}
                        title="Mensagem Portal"
                    />
                </div>
            )}

            {((loading || loadingLanguage) && <Loader text={t('common:actions.Loading')} />) || (
                <>
                    <CompanySelectionModal
                        modalState={modalState}
                        onChangeModalState={state => setModalState(state as typeof modalState)}
                        user={user}
                    />

                    <Carousel autoPlay transitionTime={5000} height={300}>
                        {slides
                            .filter(slide => slide.visible)
                            .map(slide => (
                                <Carousel.Slide
                                    key={slide.id}
                                    className="img-carousel"
                                    image={slide.image}
                                    title={slide.title ?? undefined}
                                    subtitle={slide.subtitle ?? undefined}
                                    buttonText={slide.button ?? undefined}
                                    clickAction={() =>
                                        window.open(
                                            "https://www.bradyid.com/corporate/investors/press-release-detail?releaseid=29876",
                                            "_blank",
                                            "noopener,noreferrer"
                                        )
                                    }
                                />
                            ))}
                    </Carousel>

                    <div className="container">
                        <h1>{t('common:services.Services')}</h1>
                        <div className="cards-container-2">
                            <Link to="/info/repair-center">
                                <Card>
                                    <Card.Header title={t('pages:repairCenter')}></Card.Header>
                                    <Card.Content>
                                        <Icon className="largeIcon" name="operations" root="building" exactSize={100} />
                                    </Card.Content>
                                    <Card.Footer>
                                        <Button
                                            type="link"
                                            icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                            iconPosition="right"
                                            content={t('common:actions.LearnMore')}
                                        />
                                    </Card.Footer>
                                </Card>
                            </Link>
                            <Link to="/info/contract">
                                <Card>
                                    <Card.Header title={t('pages:contracts')}></Card.Header>
                                    <Card.Content>
                                        <Icon className="largeIcon" name="document-certification" root="common" exactSize={100} />
                                    </Card.Content>
                                    <Card.Footer>
                                        <Button
                                            type="link"
                                            icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                            iconPosition="right"
                                            content={t('common:actions.LearnMore')}
                                        />
                                    </Card.Footer>
                                </Card>
                            </Link>
                            {i18n.language == 'pt' && (
                                <Link to="/info/certified">
                                    <Card className="learn-more-card">
                                        <Card.Header title={t('pages:certified')}></Card.Header>
                                        <Card.Content>
                                            <Icon className="largeIcon" name="user-operations" root="common" exactSize={100} />
                                        </Card.Content>
                                        <Card.Footer>
                                            <Button
                                                type="link"
                                                icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                                iconPosition="right"
                                                content={t('common:actions.LearnMore')}
                                            />
                                        </Card.Footer>
                                    </Card>
                                </Link>
                            )}
                        </div>
                        <div className="cards-container-2">
                            {i18n.language !== 'es' && (
                                <>
                                    <Link to="../spare-parts">
                                        <Card>
                                            <Card.Header title={t('pages:spareParts')}></Card.Header>
                                            <Card.Content>
                                                <Icon className="largeIcon" name="briefcase" root="common" exactSize={100} />
                                            </Card.Content>
                                            <Card.Footer>
                                                <Button
                                                    type="link"
                                                    icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                                    iconPosition="right"
                                                    content={t('common:actions.LearnMore')} />
                                            </Card.Footer>
                                        </Card>
                                    </Link>
                                    <Link to="/info/solutions">
                                        <Card>
                                            <Card.Header title={t('pages:solutions')}></Card.Header>
                                            <Card.Content>
                                                <Icon
                                                    className="largeIcon"
                                                    name="checkbox"
                                                    root="common"
                                                    color="blue"
                                                    exactSize={100} />
                                            </Card.Content>
                                            <Card.Footer>
                                                <Button
                                                    type="link"
                                                    icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                                    iconPosition="right"
                                                    content={t('common:actions.LearnMore')} />
                                            </Card.Footer>
                                        </Card>
                                    </Link>
                                </>
                            )}
                            <Link to="/info/instructions">
                                <Card>
                                    <Card.Header title={t('pages:instructions')}></Card.Header>
                                    <Card.Content>
                                        <Icon
                                            className="largeIcon"
                                            name="menu-physical"
                                            root="common"
                                            color="blue"
                                            exactSize={100}
                                        />
                                    </Card.Content>
                                    <Card.Footer>
                                        <Button
                                            type="link"
                                            icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                            iconPosition="right"
                                            content={t('common:actions.LearnMore')}
                                        />
                                    </Card.Footer>
                                </Card>
                            </Link>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

export default HomePage;
